import { getEmployee, getEmployeeLinkedToUser } from "@/lib/api/employees";
import { getOrganization, listOrganizations, type ListOrgsFilters } from "@/lib/api/organizations";
import { isOrgConsoleContributor } from "@/lib/orgConsoleRoles";
import type { EmployeeRow, OrganizationRow, UserRow } from "@/types/database";

export function canUserAccessAllOrganizations(user: Pick<UserRow, "user_type"> | null): boolean {
  return user?.user_type === "superadmin" || user?.user_type === "admin";
}

/**
 * Who may open the org-level MAPS overview route (`/maps-overview`).
 * Superadmin/admin do not require `org_id` on their user row — they pass `?orgId=` or use the org console MAPS tab.
 */
export function canUserViewOrgMapsAggregate(user: UserRow | null): boolean {
  if (!user) return false;
  if (user.user_type === "superadmin" || user.user_type === "admin") return true;
  if (!user.org_id) return false;
  if (user.user_type === "orgadmin") return false;
  if (user.user_type === "orguser" && isOrgConsoleContributor(user)) return true;
  return false;
}

/**
 * Lists organizations the signed-in user may see. Admins/superadmins see the full filtered list;
 * org-scoped users only ever receive their own org row (and only when filters match).
 */
export async function listOrganizationsForUser(
  user: UserRow | null,
  filters?: ListOrgsFilters
): Promise<OrganizationRow[]> {
  if (!user) return [];
  if (canUserAccessAllOrganizations(user)) {
    return listOrganizations(filters);
  }
  if ((user.user_type === "orgadmin" || user.user_type === "orguser") && user.org_id) {
    const row = await getOrganization(user.org_id);
    if (!row) return [];
    if (filters?.org_status && row.org_status !== filters.org_status) return [];
    if (filters?.orgsales_stage && row.orgsales_stage !== filters.orgsales_stage) return [];
    if (filters?.orgclient_stage && row.orgclient_stage !== filters.orgclient_stage) return [];
    return [row];
  }
  return [];
}

export async function getOrganizationForUser(
  user: Pick<UserRow, "user_type" | "org_id"> | null,
  id: string
): Promise<OrganizationRow | null> {
  const row = await getOrganization(id);
  if (!row) return null;
  if (!user) return null;
  if (canUserAccessAllOrganizations(user)) return row;
  if (user.org_id === id) return row;
  return null;
}

export type GetEmployeeForUserOptions = {
  /** When set (e.g. from org console navigation state), scopes access checks. */
  scopedOrganizationId?: string;
};

/**
 * Loads an employee row only if the current user may view that roster record (org match + role rules).
 */
export async function getEmployeeForUser(
  user: UserRow | null,
  employeeId: string,
  options?: GetEmployeeForUserOptions
): Promise<EmployeeRow | null> {
  const emp = await getEmployee(employeeId);
  if (!emp) return null;
  const scopedOrg = options?.scopedOrganizationId?.trim();
  if (scopedOrg && emp.org_id !== scopedOrg) return null;
  if (!user) return null;
  if (canUserAccessAllOrganizations(user)) return emp;
  if (!user.org_id || user.org_id !== emp.org_id) return null;
  if (user.user_type === "orgadmin") return emp;
  if (user.user_type === "orguser" && isOrgConsoleContributor(user)) return emp;
  if (user.user_type === "orguser" && user.role_type === "employee") {
    const linked = await getEmployeeLinkedToUser(emp.org_id, user.id);
    return linked?.id === employeeId ? emp : null;
  }
  return null;
}
